import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { DateTime } from "luxon";
import { fetchNotaryData } from "../../store/actions/notaryActions";
import {
  Typography,
  Box,
  Card,
  Grid2 as Grid,
  Container,
  CardContent,
  CircularProgress,
  useMediaQuery,
  Tabs,
  Tab,
  Fade,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Schedule,
  Refresh,
  CheckCircle,
  Visibility,
  Download,
} from "@mui/icons-material";
import PastNotarizationModal from "./modals/PastNotarizationModal";

const NotaryIpenManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.notarySession.user);
  const currentSession = useSelector((state) => state.sessions.currentSession);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Tab management
  const [activeTab, setActiveTab] = useState(0);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [pinDigits, setPinDigits] = useState(["", "", "", ""]);
  const [verifying, setVerifying] = useState(false);
  const [pinError, setPinError] = useState("");
  const [pastModalOpen, setPastModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Get IPEN sessions from Redux store
  const completedIpenSessions = currentUser?.completed_ipen_sessions || [];

  // Sort active IPEN sessions by booked_date (latest first)
  const activeIpenSessions = useMemo(() => {
    const sessions = currentUser?.active_ipen_sessions || [];
    return [...sessions].sort((a, b) => {
      // Try booked_date first (ISO string), then fallback to assigned_notary_at (timestamp)
      const dateA = a.booked_date
        ? new Date(a.booked_date).getTime()
        : a.assigned_notary_at
          ? a.assigned_notary_at * 1000
          : 0;
      const dateB = b.booked_date
        ? new Date(b.booked_date).getTime()
        : b.assigned_notary_at
          ? b.assigned_notary_at * 1000
          : 0;
      // Sort descending (latest first)
      return dateB - dateA;
    });
  }, [currentUser?.active_ipen_sessions]);

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  console.log("selectedSession: ", selectedSession);

  // Shared DataGrid layout
  const commonDataGridSx = {
    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
    "& .MuiDataGrid-columnHeader:focus-within": {
      outline: "none !important",
    },
    "& .MuiDataGrid-columnHeader": {
      "&:hover": {
        ".MuiDataGrid-menuIcon, .MuiDataGrid-sortIcon, .MuiDataGrid-columnHeader--sorted":
          {
            visibility: "hidden",
            pointerEvents: "none",
          },
        cursor: "default",
      },
      paddingLeft: 1,
      paddingRight: 1,
      margin: 0,
      backgroundColor: "#333",
      color: "#fff",
      borderBottom: "1px solid #ccc",
    },
    "& .MuiDataGrid-columnHeader--sortable": {
      pointerEvents: "none",
    },
    "& .MuiDataGrid-columnHeaders": {
      padding: 0,
      margin: 0,
    },
    "& .MuiDataGrid-columnHeadersInner": {
      width: "100%",
      padding: 0,
    },
    border: "1px solid #FCF2F0",
    "& .MuiDataGrid-cell": {
      borderColor: "#333333",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    "& .MuiDataGrid-columnSeparator": {
      display: "none",
    },
    ".MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
    "& .MuiDataGrid-row": {
      cursor: "pointer",
    },
  };

  // Refresh notary data (which includes IPEN sessions)
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await dispatch(fetchNotaryData(currentSession));
    } catch (error) {
      console.error("Error refreshing notary data:", error);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, currentSession]);

  useEffect(() => {
    if (currentUser?.availabilities?.length === 0) {
      navigate("/notary/finalize");
      return;
    }
    // Data is already loaded from initial notary session fetch
    if (currentUser) {
      setLoading(false);
    }
  }, [currentUser, navigate]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenModal = (session) => {
    setSelectedSession(session);
    setPinDigits(["", "", "", ""]);
    setPinError("");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSession(null);
    setPinDigits(["", "", "", ""]);
    setPinError("");
  };

  const handlePinChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) return;

    const newDigits = [...pinDigits];
    newDigits[index] = value.replace(/\D/g, ""); // Only numbers

    setPinDigits(newDigits);
    setPinError("");

    // Auto-focus next field
    if (value && index < 3) {
      const nextInput = document.getElementById(`pin-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handlePinKeyDown = (index, e) => {
    // Handle backspace to go to previous field
    if (e.key === "Backspace" && !pinDigits[index] && index > 0) {
      const prevInput = document.getElementById(`pin-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handlePinPaste = (e, index) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");

    // Extract only digits and take first 4
    const digits = pastedText.replace(/\D/g, "").slice(0, 4);

    if (digits.length > 0) {
      const newDigits = ["", "", "", ""];

      // Fill the digits starting from the current index
      for (let i = 0; i < digits.length && index + i < 4; i++) {
        newDigits[index + i] = digits[i];
      }

      setPinDigits(newDigits);
      setPinError("");

      // Focus the last filled field or the last field if all 4 are filled
      const lastFilledIndex = Math.min(index + digits.length - 1, 3);
      setTimeout(() => {
        const lastInput = document.getElementById(
          `pin-input-${lastFilledIndex}`,
        );
        if (lastInput) lastInput.focus();
      }, 0);
    }
  };

  const handleVerifyPin = async () => {
    const pin = pinDigits.join("");

    if (
      selectedSession?.notarization?.notarization?.require_ipen_pin &&
      pin.length !== 4
    ) {
      setPinError("Please enter a 4-digit PIN");
      return;
    }

    setVerifying(true);
    setPinError("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/verify-ipen-pin`,
        {
          task_id: selectedSession?.task_id || selectedSession?.id,
          signer: selectedSession.signer,
          ipen_pin: pin,
          require_pin:
            selectedSession?.notarization?.notarization?.require_ipen_pin,
        },
        {
          params: {
            currentSession,
          },
          withCredentials: true,
        },
      );

      if (response.data.success && response.data.session_url) {
        // Open session URL in new tab
        window.open(response.data.session_url, "_blank");
        handleCloseModal();
      } else {
        setPinError(response.data.message || "Invalid PIN. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying IPEN PIN:", error);
      setPinError(
        error.response?.data?.message ||
          "Failed to verify PIN. Please try again.",
      );
    } finally {
      setVerifying(false);
    }
  };

  // Handlers for completed IPEN sessions
  const handlePastNotarizationClick = (job) => {
    setSelectedJob(job);
    setPastModalOpen(true);
  };

  const handleDocumentDownload = async (job) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/download-documents`,
        {
          jobId: job.id,
          currentSession,
        },
        {
          withCredentials: true,
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `notarization_${job.id}_documents.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download documents:", error);
    }
  };

  // Base column definitions for IPEN sessions (shared between active and completed)
  // Updated to use RON-compatible structure
  const baseIpenSessionColumns = [
    {
      field: "signer_details",
      headerName: "Signer",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const signerDetails = params.row.signer_details || {};
        const firstName = signerDetails.first || "";
        const middleName = signerDetails.middle || "";
        const lastName = signerDetails.last || "";
        const fullName =
          `${firstName} ${middleName} ${lastName}`.trim() || "N/A";
        const email = signerDetails.email || "";

        // Fallback to old structure for active sessions if new structure not available
        const displayName =
          fullName !== "N/A" ? fullName : params.row.requester_name || "N/A";
        const displayEmail = email || params.row.requester_email || "";

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" fontWeight={500}>
              {displayName}
            </Typography>
            {displayEmail && (
              <Typography variant="caption" color="textSecondary">
                {displayEmail}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "signer",
      headerName: "Signer Type",
      flex: 0.7,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Chip
            label={params.value === "contractor" ? "Contractor" : "Owner"}
            size="small"
            color={params.value === "contractor" ? "primary" : "secondary"}
          />
        </Box>
      ),
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 0.9,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const phone =
          params.row.signer_details?.mobile ||
          params.row.requester_phone ||
          "N/A";
        return <Typography variant="body2">{phone}</Typography>;
      },
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1.2,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const signerDetails = params.row.signer_details || {};
        let address = "N/A";

        if (signerDetails.address || signerDetails.city) {
          const addressParts = [
            signerDetails.address,
            signerDetails.city,
            signerDetails.state,
            signerDetails.zip,
          ].filter(Boolean);
          address = addressParts.join(", ") || "N/A";
        } else if (params.row.signer_address) {
          address = params.row.signer_address;
        } else if (params.row.project_address) {
          address = params.row.project_address;
        }

        return <Typography variant="body2">{address}</Typography>;
      },
    },
  ];

  // Column definitions for active IPEN sessions
  const activeIpenSessionColumns = [
    ...baseIpenSessionColumns,
    {
      field: "booked_date",
      headerName: "Assigned Date",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        // Try booked_date first (ISO string), then fallback to assigned_notary_at (timestamp)
        if (params.row.booked_date) {
          const utcDate = DateTime.fromISO(params.row.booked_date, {
            zone: "utc",
          });
          const localDate = utcDate.setZone(
            currentUser?.timezone || "America/New_York",
          );
          const formattedDate = localDate.toFormat("MMMM dd, yyyy hh:mm a");
          return <span>{formattedDate}</span>;
        } else if (params.row.assigned_notary_at) {
          const timestamp = params.row.assigned_notary_at * 1000; // Convert to milliseconds
          const date = DateTime.fromMillis(timestamp);
          const formattedDate = date.toFormat("MMMM dd, yyyy hh:mm a");
          return <span>{formattedDate}</span>;
        }
        return "N/A";
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.8,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        return (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleOpenModal(params.row)}
            sx={{ textTransform: "none" }}
          >
            Start Session
          </Button>
        );
      },
    },
  ];

  // Column definitions for completed IPEN sessions
  const completedIpenSessionColumns = [
    ...baseIpenSessionColumns,
    {
      field: "completed_at",
      headerName: "Completed Date",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        // Try completed_at first (timestamp), then fallback to other date fields
        if (params.row.completed_at) {
          const timestamp = params.row.completed_at * 1000; // Convert to milliseconds
          const date = DateTime.fromMillis(timestamp);
          const localDate = date.setZone(
            currentUser?.timezone || "America/New_York",
          );
          const formattedDate = localDate.toFormat("MMMM dd, yyyy hh:mm a");
          return <span>{formattedDate}</span>;
        } else if (params.row.booked_date) {
          const utcDate = DateTime.fromISO(params.row.booked_date, {
            zone: "utc",
          });
          const localDate = utcDate.setZone(
            currentUser?.timezone || "America/New_York",
          );
          const formattedDate = localDate.toFormat("MMMM dd, yyyy hh:mm a");
          return <span>{formattedDate}</span>;
        } else if (params.row.assigned_notary_at) {
          const timestamp = params.row.assigned_notary_at * 1000; // Convert to milliseconds
          const date = DateTime.fromMillis(timestamp);
          const formattedDate = date.toFormat("MMMM dd, yyyy hh:mm a");
          return <span>{formattedDate}</span>;
        }
        return "N/A";
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 140,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="View Details">
              <Visibility />
            </Tooltip>
          }
          label="View"
          onClick={() => handlePastNotarizationClick(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Download Documents">
              <Download />
            </Tooltip>
          }
          label="Download"
          onClick={() => handleDocumentDownload(params.row)}
        />,
      ],
    },
  ];

  if (loading) {
    return (
      <Container maxWidth="xxl" sx={{ py: 3 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xxl" sx={{ py: 3 }}>
      <Grid container spacing={3}>
        {/* Header Section */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ backgroundColor: "#1976d2", color: "white" }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography
                    variant={mobileSize ? "h5" : "h4"}
                    fontWeight="bold"
                  >
                    IPEN Session Management
                  </Typography>
                  <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                    Manage your In-Person Electronic Notarization sessions
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Tooltip title="Refresh All Data">
                    <IconButton
                      onClick={handleRefresh}
                      disabled={refreshing}
                      sx={{ color: "white" }}
                    >
                      <Refresh
                        sx={{
                          animation: refreshing
                            ? "spin 1s linear infinite"
                            : "none",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Main Content Tabs */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant={mobileSize ? "scrollable" : "standard"}
                scrollButtons="auto"
              >
                <Tab
                  icon={<Schedule />}
                  label="Active IPEN Sessions"
                  sx={{ minWidth: mobileSize ? 120 : 160, pt: 2 }}
                />
                <Tab
                  icon={<CheckCircle />}
                  label="Completed IPEN Sessions"
                  sx={{ minWidth: mobileSize ? 120 : 160, pt: 2 }}
                />
              </Tabs>
            </Box>

            <CardContent sx={{ p: 0 }}>
              {/* Tab Panel 0: Active IPEN Sessions */}
              {activeTab === 0 && (
                <Fade in={true}>
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, display: "flex", alignItems: "center" }}
                    >
                      <Schedule sx={{ mr: 1 }} />
                      Active IPEN Sessions
                    </Typography>
                    <DataGrid
                      sx={{
                        minHeight: 400,
                        ...commonDataGridSx,
                      }}
                      rows={activeIpenSessions}
                      columns={activeIpenSessionColumns}
                      getRowId={(row) => `${row?.id}-${row?.signer}`}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 10 },
                        },
                      }}
                      pageSizeOptions={[5, 10, 25, 50]}
                      disableRowSelectionOnClick
                      rowHeight={60}
                      loading={refreshing}
                    />
                  </Box>
                </Fade>
              )}

              {/* Tab Panel 1: Completed IPEN Sessions */}
              {activeTab === 1 && (
                <Fade in={true}>
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, display: "flex", alignItems: "center" }}
                    >
                      <CheckCircle sx={{ mr: 1 }} />
                      Completed IPEN Sessions
                    </Typography>
                    <DataGrid
                      sx={{
                        minHeight: 400,
                        ...commonDataGridSx,
                      }}
                      rows={completedIpenSessions}
                      columns={completedIpenSessionColumns}
                      getRowId={(row) => `${row?.id}-${row?.signer}`}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 10 },
                        },
                      }}
                      pageSizeOptions={[5, 10, 25, 50]}
                      disableRowSelectionOnClick
                      rowHeight={60}
                      loading={refreshing}
                    />
                  </Box>
                </Fade>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* IPEN PIN Verification Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "1.5rem",
            pb: 1,
          }}
        >
          Start In-Person Notarization Session
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <Typography
              variant="body1"
              align="center"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {selectedSession?.notarization?.notarization?.require_ipen_pin
                ? "Please enter the IPEN PIN provided by the signer"
                : "This IPEN Session does not require a PIN"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mb: 2,
              }}
            >
              {[0, 1, 2, 3].map((index) => (
                <TextField
                  disabled={
                    !selectedSession?.notarization?.notarization
                      ?.require_ipen_pin
                  }
                  key={index}
                  id={`pin-input-${index}`}
                  value={pinDigits[index]}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(index, e)}
                  onPaste={(e) => handlePinPaste(e, index)}
                  slotProps={{
                    htmlInput: {
                      maxLength: 1,
                      style: {
                        textAlign: "center",
                        fontSize: "2rem",
                        fontWeight: "bold",
                        padding: "16px",
                        backgroundColor: "#f3f3f3",
                      },
                    },
                  }}
                  sx={{
                    width: "80px",
                    "& .MuiOutlinedInput-root": {
                      fontSize: "2rem",
                      height: "80px",
                      "& input": {
                        textAlign: "center",
                      },
                    },
                  }}
                  error={!!pinError}
                />
              ))}
            </Box>

            {pinError && (
              <Typography
                variant="body2"
                color="error"
                align="center"
                sx={{ mt: -2 }}
              >
                {pinError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center" }}>
          <Button
            onClick={handleCloseModal}
            variant="outlined"
            disabled={verifying}
            sx={{ minWidth: 120 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerifyPin}
            variant="contained"
            disabled={
              !selectedSession?.notarization?.notarization?.require_ipen_pin
                ? false
                : verifying || pinDigits.join("").length !== 4
            }
            sx={{ minWidth: 120 }}
          >
            {verifying ? <CircularProgress size={24} /> : "Start Session"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Past Notarization Modal for completed IPEN sessions */}
      <PastNotarizationModal
        open={pastModalOpen}
        onClose={() => {
          setPastModalOpen(false);
          setSelectedJob(null);
        }}
        selectedJob={selectedJob}
        currentUser={currentUser}
        currentSession={currentSession}
      />
    </Container>
  );
};

export default NotaryIpenManagement;
